import React, { useState } from 'react'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'

export default function BudgetAnalysis({ clients }) {
  const [viewMode, setViewMode] = useState('status') // "status", "company" or "firewalled"

  const getClientCountByAEStatus = () => {
    const clientCountByAE = clients.reduce((acc, client) => {
      const ae = client.account_executive || 'Unknown'
      if (!acc[ae]) {
        acc[ae] = { active: 0, inactive: 0, done: 0, total: 0 }
      }
      
      if (client.status === 'Active') acc[ae].active += 1
      else if (client.status === 'Inactive') acc[ae].inactive += 1
      else if (client.status === 'Done') acc[ae].done += 1
      
      acc[ae].total += 1
      return acc
    }, {})

    return Object.entries(clientCountByAE).map(([ae, counts]) => ({
      name: ae.split(' ')[0],
      fullName: ae,
      active: counts.active,
      inactive: counts.inactive,
      done: counts.done,
      total: counts.total
    })).sort((a, b) => b.total - a.total)
  }

  const getClientCountByAECompany = () => {
    const clientCountByAE = clients.reduce((acc, client) => {
      const ae = client.account_executive || 'Unknown'
      if (!acc[ae]) {
        acc[ae] = { SMG: 0, DCM: 0, DRM: 0, total: 0 }
      }
      
      if (client.company === 'SMG') acc[ae].SMG += 1
      else if (client.company === 'DCM') acc[ae].DCM += 1
      else if (client.company === 'DRM') acc[ae].DRM += 1
      
      acc[ae].total += 1
      return acc
    }, {})

    return Object.entries(clientCountByAE).map(([ae, counts]) => ({
      name: ae.split(' ')[0],
      fullName: ae,
      SMG: counts.SMG,
      DCM: counts.DCM,
      DRM: counts.DRM,
      total: counts.total
    })).sort((a, b) => b.total - a.total)
  }

  const getClientCountByAEFirewalled = () => {
  const clientCountByAE = clients.reduce((acc, client) => {
    const ae = client.account_executive || 'Unknown'
    if (!acc[ae]) {
      acc[ae] = { firewalled: 0, notFirewalled: 0, total: 0 }
    }
    
    // Check if firewalled is explicitly true, everything else counts as not firewalled
    if (client.firewalled === true) {
      acc[ae].firewalled += 1
    } else {
      acc[ae].notFirewalled += 1
    }
    
    acc[ae].total += 1
    return acc
  }, {})

    const result = Object.entries(clientCountByAE).map(([ae, counts]) => ({
      name: ae.split(' ')[0],
      fullName: ae,
      firewalled: counts.firewalled,
      notFirewalled: counts.notFirewalled,
      total: counts.total
    })).sort((a, b) => b.total - a.total)
    
    return result
  }

  const statusData = getClientCountByAEStatus()
  const companyData = getClientCountByAECompany()
  const firewalledData = getClientCountByAEFirewalled()

  return (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '12px',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <div style={{
        padding: '20px 24px',
        borderBottom: '1px solid #e5e7eb'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: 'bold',
            color: '#111827',
            margin: 0
          }}>
            Number of Clients by Account Executive
          </h3>
          <div style={{
            display: 'flex',
            backgroundColor: '#f3f4f6',
            borderRadius: '8px',
            padding: '4px'
          }}>
            <button
              onClick={() => setViewMode('status')}
              style={{
                backgroundColor: viewMode === 'status' ? 'white' : 'transparent',
                color: viewMode === 'status' ? '#111827' : '#6b7280',
                border: 'none',
                padding: '6px 12px',
                borderRadius: '4px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s',
                boxShadow: viewMode === 'status' ? '0 1px 2px rgba(0, 0, 0, 0.05)' : 'none'
              }}
            >
              By Status
            </button>
            <button
              onClick={() => setViewMode('company')}
              style={{
                backgroundColor: viewMode === 'company' ? 'white' : 'transparent',
                color: viewMode === 'company' ? '#111827' : '#6b7280',
                border: 'none',
                padding: '6px 12px',
                borderRadius: '4px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s',
                boxShadow: viewMode === 'company' ? '0 1px 2px rgba(0, 0, 0, 0.05)' : 'none'
              }}
            >
              By Company
            </button>
            <button
              onClick={() => setViewMode('firewalled')}
              style={{
                backgroundColor: viewMode === 'firewalled' ? 'white' : 'transparent',
                color: viewMode === 'firewalled' ? '#111827' : '#6b7280',
                border: 'none',
                padding: '6px 12px',
                borderRadius: '4px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s',
                boxShadow: viewMode === 'firewalled' ? '0 1px 2px rgba(0, 0, 0, 0.05)' : 'none'
              }}
            >
              By Firewalled
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={{ padding: '24px' }}>
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={viewMode === 'status' ? statusData : viewMode === 'company' ? companyData : firewalledData}>
            <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.2} />
            <XAxis dataKey="name" tick={{ fill: '#6b7280' }} fontSize={12} />
            <YAxis tick={{ fill: '#6b7280' }} fontSize={12} />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: 'rgba(31, 41, 55, 0.9)', 
                backdropFilter: 'blur(4px)', 
                border: '1px solid #4b5563', 
                borderRadius: '8px',
                color: '#f3f4f6'
              }}
              cursor={{ fill: '#f3f4f6', fillOpacity: 0.2 }}
              formatter={(value, name) => [value, name]}
              labelFormatter={(label) => {
                const data = viewMode === 'status' ? statusData : companyData
                const item = data.find(d => d.name === label)
                return item ? item.fullName : label
              }}
            />
            {viewMode === 'status' ? (
            <>
              <Bar dataKey="active" stackId="a" fill="#22C55E" name="Active Clients" />
              <Bar dataKey="inactive" stackId="a" fill="#6B7280" name="Inactive Clients" />
              <Bar dataKey="done" stackId="a" fill="#3B82F6" name="Done Clients" />
            </>
          ) : viewMode === 'company' ? (
            <>
              <Bar dataKey="SMG" stackId="a" fill="#FFFF00" name="SMG Clients" />
              <Bar dataKey="DCM" stackId="a" fill="#000000" name="DCM Clients" />
              <Bar dataKey="DRM" stackId="a" fill="#858585" name="DRM Clients" />
            </>
          ) : (
            <>
              <Bar dataKey="firewalled" stackId="a" fill="#DC2626" name="Firewalled" />
              <Bar dataKey="notFirewalled" stackId="a" fill="#059669" name="Not Firewalled" />
            </>
          )}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}